/**
 * Core resumable upload logic extracted from gcsClientUpload.ts
 * Provides reusable functions for resumable GCS uploads
 */

import { formatError } from '@/utils/error';
import * as Sentry from '@sentry/remix';

type StatusCallback = (message: string) => void;

const DEFAULT_CHUNK_SIZE = 8 * 1024 * 1024; // 8MB chunks for resumable uploads
const DEFAULT_RETRIES = 5;

/**
 * Utility function to wait for a specified number of milliseconds
 */
export const wait = (ms: number, signal?: AbortSignal) =>
  new Promise((resolve, reject) => {
    const timeout = setTimeout(resolve, ms);

    if (signal) {
      if (signal.aborted) {
        clearTimeout(timeout);
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }

      signal.addEventListener('abort', () => {
        clearTimeout(timeout);
        reject(new DOMException('Aborted', 'AbortError'));
      });
    }
  });

/**
 * Parse the Range header from a resumable upload response
 * Returns the next byte offset to resume from
 */
export function parseRange(rangeHeader: string | null): number {
  if (!rangeHeader) {
    return 0;
  }

  const match = rangeHeader.match(/bytes=(\d+)-(\d+)/);

  if (!match) {
    return 0;
  }

  const [, , end] = match;
  return Number(end) + 1;
}

/**
 * Query the current upload offset from the server
 * Used to resume uploads after errors
 */
export async function queryUploadOffset(uploadUrl: string, totalSize: number, signal?: AbortSignal): Promise<number> {
  const statusResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Length': '0',
      'Content-Range': `bytes */${totalSize}`,
    },
    signal,
  });

  if (statusResponse.status === 308) {
    return parseRange(statusResponse.headers.get('Range'));
  }

  // If 200/201 returned the upload is already complete
  if (statusResponse.ok) {
    return totalSize;
  }

  return 0;
}

/**
 * Start a resumable upload session
 * @param params - Upload session parameters
 * @param apiRoute - API route to use for creating the session (default: '/api/gcs-resumable')
 * @returns The resumable upload URL
 */
export async function startResumableSession(
  params: {
    bucketName?: string;
    objectName: string;
    contentType: string;
    contentLength?: number;
    assetType?: 'image' | 'video';
  },
  apiRoute: string = '/api/gcs-resumable',
  signal?: AbortSignal,
): Promise<string> {
  Sentry.addBreadcrumb({
    category: 'resumable-upload',
    message: 'Starting resumable upload session',
    data: {
      objectName: params.objectName,
      contentType: params.contentType,
      contentLength: params.contentLength,
      bucketName: params.bucketName,
      assetType: params.assetType,
    },
    level: 'info',
  });

  try {
    const response = await fetch(apiRoute, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
      signal,
    });

    if (!response.ok) {
      const errorBody = await response.text();
      const error = new Error(`Failed to create resumable session: ${errorBody}`);
      Sentry.captureException('Failed to create resumable session', {
        level: 'error',
        extra: {
          objectName: params.objectName,
          error: formatError(errorBody),
        },
      });
      throw error;
    }

    const { uploadUrl } = (await response.json()) as { uploadUrl: string };

    if (!uploadUrl) {
      const error = new Error('Upload URL missing from resumable session response.');
      Sentry.captureException(error, {
        tags: {
          action: 'resumable_session_create',
          assetType: params.assetType || 'unknown',
        },
        extra: {
          objectName: params.objectName,
          apiRoute,
        },
      });
      throw error;
    }

    Sentry.addBreadcrumb({
      category: 'resumable-upload',
      message: 'Resumable upload session created successfully',
      data: {
        objectName: params.objectName,
        uploadUrl: uploadUrl.split('?')[0], // Remove query params for privacy
      },
      level: 'info',
    });

    return uploadUrl;
  } catch (error) {
    console.error('[resumableUpload] Error creating resumable session:', error);
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: {
        action: 'resumable_session_create',
        assetType: params.assetType || 'unknown',
      },
      extra: {
        objectName: params.objectName,
        apiRoute,
      },
    });
    throw new Error(`Failed to create resumable session: ${error}`);
  }
}

export interface UploadStats {
  totalSize: number;
  sizeMB: string;
  durationMs: number;
  durationSec: string;
  speedMBps: string;
  generation?: string;
}

/**
 * Upload a blob using resumable upload protocol
 * Handles chunking, retries, and progress tracking
 */
export async function uploadBlobResumable(
  uploadUrl: string,
  blob: Blob,
  {
    chunkSize = DEFAULT_CHUNK_SIZE,
    maxRetries = DEFAULT_RETRIES,
    onStatus,
    contentType,
    signal,
  }: {
    chunkSize?: number;
    maxRetries?: number;
    onStatus?: StatusCallback;
    contentType?: string;
    signal?: AbortSignal;
  } = {},
): Promise<UploadStats> {
  const totalSize = blob.size;
  let offset = 0;
  const mimeType = contentType || blob.type || 'application/octet-stream';
  const uploadStartTime = Date.now();

  Sentry.addBreadcrumb({
    category: 'resumable-upload',
    message: 'Starting blob upload',
    data: {
      totalSize,
      sizeMB: (totalSize / (1024 * 1024)).toFixed(2),
      chunkSize,
      maxRetries,
      contentType: mimeType,
    },
    level: 'info',
  });

  onStatus?.(`Starting upload of ${(totalSize / (1024 * 1024)).toFixed(2)} MB...`);

  let gcsGeneration: string | undefined;

  while (offset < totalSize) {
    const end = Math.min(offset + chunkSize, totalSize);
    const chunk = blob.slice(offset, end);
    const contentRange = `bytes ${offset}-${end - 1}/${totalSize}`;
    let attempt = 0;
    console.log('offset , end , totalSize >>>', offset, end, totalSize);

    while (attempt < maxRetries) {
      if (signal?.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }

      try {
        const response = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': mimeType,
            'Content-Range': contentRange,
          },
          body: chunk,
          signal,
        });

        if (response.status === 308) {
          console.log('response.status === 308 >>>', response.status);
          offset = parseRange(response.headers.get('Range'));
          onStatus?.(`Upload continuing at byte ${offset} of ${totalSize}`);
          break;
        }

        if (response.ok) {
          console.log('response.ok >>>', response.ok);

          try {
            const metadata = (await response.json()) as { generation?: string };
            gcsGeneration = metadata.generation;
            console.log('generation id: ', gcsGeneration);
          } catch {
            console.warn('[resumableUpload] Could not parse final response JSON for generation');
            Sentry.captureMessage('GCS finalize response JSON parse failed — generation will be missing', {
              level: 'warning',
              tags: { action: 'parse_gcs_generation' },
            });
          }

          offset = end;
          onStatus?.(`Uploaded ${Math.min(offset, totalSize)} of ${totalSize} bytes`);
          break;
        }

        console.log('error >>>', response);

        const error = new Error(`Unexpected status ${response.status} while uploading chunk.`);

        // Transient chunk error — record as a breadcrumb only. It gets retried
        // below; if all retries are exhausted the final captureException (in the
        // catch block) is what actually reports the issue. Capturing here would
        // create a Sentry issue even for chunks that succeed on retry (noise).
        Sentry.addBreadcrumb({
          category: 'resumable-upload',
          message: 'Unexpected status while uploading chunk',
          level: 'warning',
          data: {
            httpStatus: response.status,
            offset,
            end,
            totalSize,
            chunkSize: end - offset,
            attempt: attempt + 1,
            contentRange,
            error: formatError(error),
          },
        });
        throw error;
      } catch (error) {
        attempt += 1;

        const backoff = Math.min(1000 * 2 ** attempt, 8000);
        onStatus?.(`Retrying chunk at byte ${offset} after error. Backoff ${backoff}ms.`);

        Sentry.addBreadcrumb({
          category: 'resumable-upload',
          message: `Retrying chunk upload (attempt ${attempt}/${maxRetries})`,
          data: {
            offset,
            end,
            totalSize,
            attempt,
            backoff,
            error: formatError(error),
          },
          level: 'error',
        });

        await wait(backoff, signal);

        const serverOffset = await queryUploadOffset(uploadUrl, totalSize, signal);
        console.log('serverOffset >>>', serverOffset);
        offset = Math.max(offset, serverOffset);

        if (attempt >= maxRetries) {
          Sentry.captureException('Upload failed after max retries.', {
            level: 'error',
            tags: {
              action: 'resumable_upload_failed',
              failureReason: 'max_retries_exceeded',
            },
            extra: {
              offset,
              end,
              totalSize,
              chunkSize: end - offset,
              maxRetries,
              finalAttempt: attempt,
              error: formatError(error),
            },
          });
          throw error;
        }

        break;
      }
    }
  }

  const uploadEndTime = Date.now();
  const durationMs = uploadEndTime - uploadStartTime;
  const durationSec = (durationMs / 1000).toFixed(2);
  const sizeMB = (totalSize / (1024 * 1024)).toFixed(2);
  const speedMBps = (totalSize / (1024 * 1024) / (durationMs / 1000)).toFixed(2);

  onStatus?.(`Upload completed in ${durationSec}s (${sizeMB} MB at ${speedMBps} MB/s)`);

  Sentry.addBreadcrumb({
    category: 'resumable-upload',
    message: 'Blob upload completed successfully',
    data: {
      totalSize,
      sizeMB,
      durationMs,
      durationSec,
      speedMBps,
      uploadUrl: uploadUrl.split('?')[0], // Remove query params for privacy
    },
    level: 'info',
  });

  console.log('[resumableUpload] Upload statistics:', {
    totalSize,
    sizeMB,
    durationMs,
    durationSec,
    speedMBps,
    uploadUrl: uploadUrl.split('?')[0],
  });

  return {
    totalSize,
    sizeMB,
    durationMs,
    durationSec,
    speedMBps,
    generation: gcsGeneration,
  };
}
